import { getEntityLabel, type EntityCopyKey } from './entities';

export const dialogMessages = {
  'entity.add.title': ({ entity }: { entity: EntityCopyKey }) =>
    `Add ${getEntityLabel(entity, { case: 'title' })}`,
  'entity.addTo.title': ({ entity, owner }: { entity: EntityCopyKey; owner: EntityCopyKey }) =>
    `Add ${getEntityLabel(entity, { case: 'title' })} To ${getEntityLabel(owner, { case: 'title' })}`,
  'entity.edit.title': ({ entity, plural = false }: { entity: EntityCopyKey; plural?: boolean }) =>
    `Edit ${getEntityLabel(entity, { case: 'title', plural })}`,
  'entity.confirmDelete.title': 'Confirm Delete',
  'destructive.cannotBeUndone': 'This action cannot be undone.',
  'destructive.delete.confirmLabel': ({ entity }: { entity: EntityCopyKey }) =>
    `Delete ${getEntityLabel(entity, { case: 'title' })}`,
  'destructive.delete.simpleDescription': ({ entity }: { entity: EntityCopyKey }) =>
    `This ${getEntityLabel(entity)} will be deleted.`,
  'destructive.delete.title': ({ entity }: { entity: EntityCopyKey }) =>
    `Delete ${getEntityLabel(entity, { case: 'title' })}`,
  'destructive.deleteNamed.confirmLabel': ({ label }: { label: string }) => `Delete ${label}`,
  'destructive.deleteNamed.title': ({ label }: { label: string }) => `Delete ${label}`,
  'destructive.delete.permanentDescription': ({ entity }: { entity: EntityCopyKey }) =>
    `This will permanently delete the ${getEntityLabel(entity)}. This action cannot be undone.`,
  'destructive.remove.confirmLabel': ({ entity }: { entity: EntityCopyKey }) =>
    `Remove ${getEntityLabel(entity, { case: 'title' })}`,
  'destructive.remove.title': ({ entity }: { entity: EntityCopyKey }) =>
    `Remove ${getEntityLabel(entity, { case: 'title' })}`,
  'destructive.revokeNamed.confirmLabel': ({ label }: { label: string }) => `Revoke ${label}`,
  'destructive.revokeNamed.title': ({ label }: { label: string }) => `Revoke ${label}`,
  'destructive.wipeData.confirmLabel': 'Wipe Data',
  'destructive.wipeData.title': ({ label }: { label: string }) => `Wipe ${label} Data`,
  'destructive.typeNameToConfirm': ({ name }: { name: string }) => `Enter ${name} to confirm.`,
  'linking.link.title': ({ entity, owner }: { entity: EntityCopyKey; owner: EntityCopyKey }) =>
    `Link ${getEntityLabel(entity, { case: 'title' })} to ${getEntityLabel(owner, { case: 'title' })}`,
  'linking.link.selectedDescription': ({
    entity,
    owner,
  }: {
    entity: EntityCopyKey;
    owner?: EntityCopyKey;
  }) =>
    owner
      ? `The selected ${getEntityLabel(entity)} will be linked to this ${getEntityLabel(owner)}.`
      : `The selected ${getEntityLabel(entity)} will be linked.`,
  'linking.link.withSelectedChildrenDescription': ({
    entity,
    child,
    owner,
  }: {
    entity: EntityCopyKey;
    child: EntityCopyKey;
    owner: EntityCopyKey;
  }) =>
    `Linking this ${getEntityLabel(entity)} will also link the selected ${getEntityLabel(child, { plural: true })} to this ${getEntityLabel(owner)}.`,
  'linking.unlink.title': ({
    entity,
    owner,
    plural = true,
  }: {
    entity: EntityCopyKey;
    owner: EntityCopyKey;
    plural?: boolean;
  }) =>
    `Unlink ${getEntityLabel(entity, { case: 'title', plural })} from ${getEntityLabel(owner, {
      case: 'title',
    })}`,
  'linking.unlink.selectedDescription': ({
    entity,
    owner,
    plural = true,
  }: {
    entity: EntityCopyKey;
    owner?: EntityCopyKey;
    plural?: boolean;
  }) => {
    const entityLabel = getEntityLabel(entity, { plural });
    if (plural) {
      return owner
        ? `Selected ${entityLabel} will be unlinked from this ${getEntityLabel(owner)}.`
        : `Selected ${entityLabel} will be unlinked.`;
    }

    return owner
      ? `The selected ${entityLabel} will be unlinked from this ${getEntityLabel(owner)}.`
      : `The selected ${entityLabel} will be unlinked.`;
  },
  'linking.unlink.keepSelectedChildrenDescription': ({
    child,
    owner,
  }: {
    child: EntityCopyKey;
    owner: EntityCopyKey;
  }) =>
    `Selected ${getEntityLabel(child, { plural: true })} will be unlinked from this ${getEntityLabel(
      owner,
    )}. Uncheck ${getEntityLabel(child, { plural: true })} to keep them linked.`,
} as const;

export type DialogMessageCatalog = typeof dialogMessages;
export type DialogMessageKey = keyof DialogMessageCatalog;

export type DialogMessageArgs<K extends DialogMessageKey> = DialogMessageCatalog[K] extends (
  args: infer TArgs,
) => string
  ? TArgs
  : undefined;

export const getDialogMessage = <K extends DialogMessageKey>(
  key: K,
  args?: DialogMessageArgs<K>,
): string => {
  const entry = dialogMessages[key];
  if (typeof entry === 'function') {
    return (entry as (messageArgs: DialogMessageArgs<K>) => string)(
      args ?? ({} as DialogMessageArgs<K>),
    );
  }
  return entry;
};

type DialogSampleArgs = {
  [K in DialogMessageKey]?: DialogMessageArgs<K>;
};

const sampleArgs: DialogSampleArgs = {
  'entity.add.title': { entity: 'project' },
  'entity.addTo.title': { entity: 'test', owner: 'suite' },
  'entity.edit.title': { entity: 'project' },
  'destructive.delete.confirmLabel': { entity: 'project' },
  'destructive.delete.simpleDescription': { entity: 'suite' },
  'destructive.delete.title': { entity: 'project' },
  'destructive.deleteNamed.confirmLabel': { label: 'Upload Token' },
  'destructive.deleteNamed.title': { label: 'Upload Token' },
  'destructive.delete.permanentDescription': { entity: 'project' },
  'destructive.remove.confirmLabel': { entity: 'user' },
  'destructive.remove.title': { entity: 'user' },
  'destructive.revokeNamed.confirmLabel': { label: 'Webhook Key' },
  'destructive.revokeNamed.title': { label: 'Webhook Key' },
  'destructive.wipeData.title': { label: 'Organisation' },
  'destructive.typeNameToConfirm': { name: 'Acme' },
  'linking.link.title': { entity: 'test', owner: 'suite' },
  'linking.link.selectedDescription': { entity: 'test', owner: 'suite' },
  'linking.link.withSelectedChildrenDescription': {
    entity: 'suite',
    child: 'test',
    owner: 'release',
  },
  'linking.unlink.title': { entity: 'test', owner: 'suite' },
  'linking.unlink.selectedDescription': { entity: 'test', owner: 'suite' },
  'linking.unlink.keepSelectedChildrenDescription': { child: 'test', owner: 'feature' },
};

const getDialogMessageSample = <K extends DialogMessageKey>(
  key: K,
  args: DialogMessageArgs<K> | undefined,
): { key: DialogMessageKey; value: string } => ({
  key,
  value: getDialogMessage(key, args),
});

export const getDialogMessageSamples = (): Array<{ key: DialogMessageKey; value: string }> =>
  (Object.keys(dialogMessages) as DialogMessageKey[]).map((key) => ({
    key,
    value: getDialogMessageSample(key, sampleArgs[key] as never).value,
  }));

export const getLinkDialogTitle = (label: string, contextLabel?: string): string =>
  contextLabel ? `Link ${label} to ${contextLabel}` : `Link ${label}`;

export const getUnlinkDialogTitle = (
  label: string,
  contextLabel?: string,
  options: { plural?: boolean } = {},
): string => {
  const entityLabel = options.plural === false ? label : `${label}s`;
  return contextLabel ? `Unlink ${entityLabel} from ${contextLabel}` : `Unlink ${entityLabel}`;
};

export const getLinkDialogDescription = (label: string, contextLabel?: string): string => {
  const lowerLabel = label.toLowerCase();
  return contextLabel
    ? `The selected ${lowerLabel} will be linked to this ${contextLabel.toLowerCase()}.`
    : `The selected ${lowerLabel} will be linked.`;
};

export const getUnlinkDialogDescription = (
  label: string,
  contextLabel?: string,
  options: { itemLabel?: string; plural?: boolean } = {},
): string => {
  const lowerEntityLabel =
    options.plural === false
      ? label.toLowerCase()
      : (options.itemLabel ?? `${label}s`).toLowerCase();

  if (options.plural === false) {
    return contextLabel
      ? `The selected ${lowerEntityLabel} will be unlinked from this ${contextLabel.toLowerCase()}.`
      : `The selected ${lowerEntityLabel} will be unlinked.`;
  }

  return contextLabel
    ? `Selected ${lowerEntityLabel} will be unlinked from this ${contextLabel.toLowerCase()}.`
    : `Selected ${lowerEntityLabel} will be unlinked.`;
};
